import { useCallback, useEffect, useMemo, useState } from "react";

import { fetchOrders, updateOrderStatus } from "@/features/admin/api";
import { useAdminSession } from "@/features/admin/useAdminSession";
import type { AdminOrder } from "@/features/admin/types";
import PaginationControls from "./PaginationControls";

type OrderStatus = AdminOrder["statut"];

const statusConfig: Record<OrderStatus, { label: string; cls: string; icon: string }> = {
  en_attente: { label: "En attente", cls: "bg-amber-100 text-amber-600", icon: "ri-time-line" },
  confirme: { label: "Confirmé", cls: "bg-blue-100 text-blue-600", icon: "ri-checkbox-circle-line" },
  expedie: { label: "Expédié", cls: "bg-purple-100 text-purple-600", icon: "ri-truck-line" },
  livre: { label: "Livré", cls: "bg-emerald-100 text-emerald-600", icon: "ri-map-pin-2-line" },
  annule: { label: "Annulé", cls: "bg-red-100 text-red-500", icon: "ri-close-circle-line" }
};

const statusFlow: OrderStatus[] = ["en_attente", "confirme", "expedie", "livre"];

const paymentStatusConfig: Record<AdminOrder["paymentStatus"], { label: string; cls: string }> = {
  en_attente: { label: "Paiement en attente", cls: "bg-amber-100 text-amber-700" },
  en_cours: { label: "Paiement en cours", cls: "bg-blue-100 text-blue-700" },
  reussi: { label: "Paiement validé", cls: "bg-emerald-100 text-emerald-700" },
  echoue: { label: "Paiement échoué", cls: "bg-red-100 text-red-700" }
};
const ORDERS_PER_PAGE = 5;

export default function CommandesTab() {
  const { request } = useAdminSession();
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [filter, setFilter] = useState<"tous" | OrderStatus>("tous");
  const [selected, setSelected] = useState<AdminOrder | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const nextOrders = await fetchOrders(request);
      setOrders(nextOrders);
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Impossible de charger les commandes";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    void loadOrders();
  }, [loadOrders]);

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 3000);
  };

  const handleAdvance = async (order: AdminOrder) => {
    const currentIndex = statusFlow.indexOf(order.statut);

    if (currentIndex < 0 || currentIndex >= statusFlow.length - 1) {
      return;
    }

    try {
      await updateOrderStatus(request, order.id, statusFlow[currentIndex + 1]);
      setSelected(null);
      showToast("Statut mis à jour");
      await loadOrders();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Mise à jour impossible";
      setError(message);
    }
  };

  const handleCancel = async (order: AdminOrder) => {
    try {
      await updateOrderStatus(request, order.id, "annule");
      setSelected(null);
      showToast("Commande annulée");
      await loadOrders();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Annulation impossible";
      setError(message);
    }
  };

  const filtered = useMemo(() => {
    return orders.filter((order) => filter === "tous" || order.statut === filter);
  }, [filter, orders]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / ORDERS_PER_PAGE));
  const paginatedOrders = useMemo(() => {
    const startIndex = (currentPage - 1) * ORDERS_PER_PAGE;
    return filtered.slice(startIndex, startIndex + ORDERS_PER_PAGE);
  }, [currentPage, filtered]);

  const totalRevenu = useMemo(() => {
    return orders.filter((order) => order.statut !== "annule").reduce((accumulator, order) => accumulator + order.total, 0);
  }, [orders]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  return (
    <div className="space-y-6 relative">
      {toast && (
        <div className="fixed top-6 right-6 z-50 px-5 py-3 rounded-xl shadow-lg bg-emerald-500 text-white text-sm font-medium flex items-center gap-2">
          <i className="ri-checkbox-circle-line text-lg"></i>
          {toast}
        </div>
      )}

      <div>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-[#1A1A1A]">Gestion des Commandes</h1>
            <p className="text-gray-500 text-sm mt-1">Suivez, confirmez et faites avancer les commandes de la boutique</p>
            {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
          </div>
          <button
            onClick={() => void loadOrders()}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#E8521A] text-white text-sm font-medium hover:bg-[#d44a16] transition-colors whitespace-nowrap self-start sm:self-auto"
          >
            <i className="ri-refresh-line"></i>
            Actualiser
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
        {(Object.keys(statusConfig) as OrderStatus[]).map((status) => {
          const count = orders.filter((order) => order.statut === status).length;
          const config = statusConfig[status];

          return (
            <div key={status} className="bg-white rounded-2xl p-4 border border-gray-100">
              <div className={`w-9 h-9 flex items-center justify-center rounded-xl mb-3 ${config.cls}`}>
                <i className={`${config.icon} text-base`}></i>
              </div>
              <div className="text-2xl font-bold text-[#1A1A1A]">{count}</div>
              <div className="text-xs text-gray-500 mt-0.5">{config.label}</div>
            </div>
          );
        })}
      </div>

      <div className="bg-gradient-to-r from-[#E8521A] to-[#f07a4a] rounded-2xl p-5 text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm opacity-80 mb-1">Revenu total (commandes actives)</div>
            <div className="text-3xl font-bold">{totalRevenu.toLocaleString()} FCFA</div>
          </div>
          <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-white/20">
            <i className="ri-money-cny-circle-line text-2xl"></i>
          </div>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {(["tous", ...Object.keys(statusConfig)] as Array<"tous" | OrderStatus>).map((value) => (
          <button
            key={value}
            onClick={() => setFilter(value)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
              filter === value
                ? "bg-[#E8521A] text-white"
                : "bg-white text-gray-600 border border-gray-200 hover:border-[#E8521A] hover:text-[#E8521A]"
            }`}
          >
            {value === "tous" ? "Toutes" : statusConfig[value].label}
            <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${filter === value ? "bg-white/20" : "bg-gray-100"}`}>
              {value === "tous" ? orders.length : orders.filter((order) => order.statut === value).length}
            </span>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px]">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-5 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Commande</th>
                <th className="text-left px-5 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Membre</th>
                <th className="text-left px-5 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Produits</th>
                <th className="text-left px-5 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Total</th>
                <th className="text-left px-5 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                <th className="text-left px-5 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Statut</th>
                <th className="text-right px-5 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                Array.from({ length: 6 }).map((_, index) => (
                  <tr key={index}>
                    <td colSpan={7} className="px-5 py-4">
                      <div className="h-14 rounded-xl bg-gray-100 animate-pulse"></div>
                    </td>
                  </tr>
                ))
              ) : paginatedOrders.map((order) => {
                const status = statusConfig[order.statut];
                const index = statusFlow.indexOf(order.statut);
                const canAdvance = index >= 0 && index < statusFlow.length - 1;

                return (
                  <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-4 text-sm font-mono font-semibold text-[#E8521A]">{order.code}</td>
                    <td className="px-5 py-4">
                      <div className="text-sm font-semibold text-[#1A1A1A]">{order.membre}</div>
                      <div className="text-xs text-gray-500">{order.email ?? "Email non renseigné"}</div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="text-sm text-gray-600">
                        {order.produits.slice(0, 2).map((product) => (
                          <div key={`${order.id}-${product.nom}`} className="text-xs">{product.nom} x{product.quantite}</div>
                        ))}
                        {order.produits.length > 2 ? <div className="text-xs text-gray-400">+{order.produits.length - 2} autres</div> : null}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm font-bold text-[#1A1A1A]">{order.total.toLocaleString()} FCFA</td>
                    <td className="px-5 py-4 text-sm text-gray-500">{order.date}</td>
                    <td className="px-5 py-4">
                      <div className="flex flex-wrap gap-2">
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${status.cls}`}>{status.label}</span>
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${paymentStatusConfig[order.paymentStatus].cls}`}>
                          {paymentStatusConfig[order.paymentStatus].label}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => setSelected(order)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-600 hover:bg-orange-100 hover:text-[#E8521A] transition-colors"
                          title="Voir détails"
                        >
                          <i className="ri-eye-line text-sm"></i>
                        </button>
                        {canAdvance ? (
                          <button
                            onClick={() => void handleAdvance(order)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-100 text-emerald-600 hover:bg-emerald-200 transition-colors"
                            title="Avancer le statut"
                          >
                            <i className="ri-arrow-right-line text-sm"></i>
                          </button>
                        ) : null}
                        {order.statut !== "annule" && order.statut !== "livre" ? (
                          <button
                            onClick={() => void handleCancel(order)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-400 hover:bg-red-100 transition-colors"
                            title="Annuler"
                          >
                            <i className="ri-close-line text-sm"></i>
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!loading && filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <i className="ri-shopping-cart-2-line text-4xl mb-3 block"></i>
              <p className="text-sm">Aucune commande trouvée</p>
            </div>
          ) : null}
        </div>
      </div>

      {!loading && filtered.length > ORDERS_PER_PAGE ? (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemLabel="commandes"
        />
      ) : null}

      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-bold text-[#1A1A1A] text-lg">Commande {selected.code}</h3>
              <button onClick={() => setSelected(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-xl p-3">
                  <div className="text-xs text-gray-500 mb-1">Membre</div>
                  <div className="text-sm font-semibold text-[#1A1A1A]">{selected.membre}</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <div className="text-xs text-gray-500 mb-1">Date</div>
                  <div className="text-sm font-semibold text-[#1A1A1A]">{selected.date}</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <div className="text-xs text-gray-500 mb-1">Paiement</div>
                  <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${paymentStatusConfig[selected.paymentStatus].cls}`}>
                    {paymentStatusConfig[selected.paymentStatus].label}
                  </span>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <div className="text-xs text-gray-500 mb-1">Référence</div>
                  <div className="text-sm font-semibold text-[#1A1A1A]">{selected.paymentReference ?? "Non disponible"}</div>
                </div>
              </div>

              {selected.statut === "annule" ? (
                <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-500">
                  Cette commande a été annulée.
                </div>
              ) : null}

              <div>
                <div className="text-sm font-semibold text-[#1A1A1A] mb-2">Produits commandés</div>
                <div className="space-y-2">
                  {selected.produits.map((product) => (
                    <div key={`${selected.id}-${product.nom}`} className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
                      <div>
                        <div className="text-sm font-medium text-[#1A1A1A]">{product.nom}</div>
                        <div className="text-xs text-gray-500">Qté: {product.quantite}</div>
                      </div>
                      <div className="text-sm font-bold text-[#E8521A]">{(product.prix * product.quantite).toLocaleString()} FCFA</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between bg-orange-50 rounded-xl px-4 py-3">
                <span className="font-bold text-[#1A1A1A]">Total</span>
                <span className="font-bold text-[#E8521A] text-lg">{selected.total.toLocaleString()} FCFA</span>
              </div>

              <div>
                <div className="text-sm font-semibold text-[#1A1A1A] mb-3">Progression</div>
                <div className="flex items-center gap-1">
                  {statusFlow.map((status, index) => {
                    const currentIndex = statusFlow.indexOf(selected.statut);
                    const isDone = currentIndex >= 0 && index <= currentIndex;
                    const config = statusConfig[status];

                    return (
                      <div key={status} className="flex items-center flex-1">
                        <div className={`flex-1 h-1.5 rounded-full ${isDone ? "bg-[#E8521A]" : "bg-gray-200"}`}></div>
                        <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 ${isDone ? "bg-[#E8521A] text-white" : "bg-gray-200 text-gray-400"}`}>
                          <i className={`${config.icon} text-xs`}></i>
                        </div>
                        {index < statusFlow.length - 1 ? (
                          <div className={`flex-1 h-1.5 rounded-full ${index < currentIndex ? "bg-[#E8521A]" : "bg-gray-200"}`}></div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between mt-1">
                  {statusFlow.map((status) => (
                    <span key={status} className="text-xs text-gray-400 text-center flex-1">{statusConfig[status].label}</span>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                {(() => {
                  const currentIndex = statusFlow.indexOf(selected.statut);
                  const canAdvance = currentIndex >= 0 && currentIndex < statusFlow.length - 1;

                  return canAdvance ? (
                    <button
                      onClick={() => void handleAdvance(selected)}
                      className="flex-1 py-3 rounded-xl bg-[#E8521A] text-white text-sm font-semibold hover:bg-[#d44a16] transition-colors"
                    >
                      Passer à l&apos;étape suivante
                    </button>
                  ) : null;
                })()}
                {selected.statut !== "annule" && selected.statut !== "livre" ? (
                  <button
                    onClick={() => void handleCancel(selected)}
                    className="flex-1 py-3 rounded-xl border border-red-200 text-red-500 text-sm font-semibold hover:bg-red-50 transition-colors"
                  >
                    Annuler la commande
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
